import React, { memo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { queryClient } from '../../Root';
import { BASE_PATH, MIETER_PLUS_PATH, WELCOME_MODAL, WOHNEN_PLUS_PATH } from '@/domain/routes';
import { useCheckShouldDisplayWelcomeModal } from '@/customHooks/useCheckShouldDisplayWelcomeModal.hook';
import { useGetBenefitsListPage } from '@/customHooks/useGetBenefitsListPage.hook';
import { SuccessModal } from '@/components/content/successModal/SuccessModal';
import WohnenPlusUpsellModal from '@/components/content/welcomeModal/wohnenUpsell/WohnenPlusUpsellModal';
import { WohnenPlusLightUpsellModal } from '@/components/content/benefits/modals/wohnenPlusLight/WohnenPlusLightUpsellModal';
import { useWohnenPlusLightMeinPlus } from '@/components/content/benefits/modals/wohnenPlusLight/useWohnenPlusLightMeinPlus.hook';
import { useGetUpsellOffer } from '@/apiHooks/upsellOffer.hook';
import { OfferType, UpsellOfferOrigin } from '@/@types/upsellOffer';
import { useQueryParam } from '@/customHooks/useQueryParam.hook';

const WelcomeModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const benefitsPageListPage = useGetBenefitsListPage();
  useCheckShouldDisplayWelcomeModal(benefitsPageListPage);
  const isFirstTimeWelcome = location.pathname?.includes(WELCOME_MODAL);
  const isMieterPlusModal = location.pathname?.includes(MIETER_PLUS_PATH);

  const { showWohnenPlusLight, onCloseWohnenPlusLight } = useWohnenPlusLightMeinPlus();
  const hasLivingUpsellParam = useQueryParam('livingUpsell') === 'true';
  const { upsellOffer, initializing } = useGetUpsellOffer(
    UpsellOfferOrigin.MEIN_PLUS,
    OfferType.WOHNEN_PLUS,
    isFirstTimeWelcome,
  );
  const showWohnenPlusUpsell = !!(upsellOffer?.isEligible && (upsellOffer?.showOffer || hasLivingUpsellParam));

  const onCloseWohnenPlusUpsell = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['upsell-offer'] });
    const welcomePath = isFirstTimeWelcome ? '/willkommen' : '';
    navigate(
      isMieterPlusModal
        ? `${BASE_PATH}${MIETER_PLUS_PATH}${welcomePath}`
        : `${BASE_PATH}${WOHNEN_PLUS_PATH}${welcomePath}`,
    );
  }, [isMieterPlusModal, isFirstTimeWelcome]);

  const onCloseSuccessModal = useCallback(() => {
    navigate(isMieterPlusModal ? `${BASE_PATH}${MIETER_PLUS_PATH}` : `${BASE_PATH}${WOHNEN_PLUS_PATH}`);
  }, [isMieterPlusModal]);

  useEffect(() => {
    if (showWohnenPlusUpsell && !hasLivingUpsellParam && !showWohnenPlusLight) {
      queryClient.invalidateQueries({
        queryKey: ['upsell-offer', UpsellOfferOrigin.MEIN_PLUS, OfferType.WOHNEN_PLUS],
        exact: true,
      });
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('livingUpsell', 'true');
      setSearchParams(newSearchParams);
    }
  }, [showWohnenPlusUpsell]);

  // Priority: WP+ upsell > first-time welcome > WP+ Light
  const shouldShowAnyModal = showWohnenPlusUpsell || (isFirstTimeWelcome && !initializing) || showWohnenPlusLight;

  if (!shouldShowAnyModal) {
    return null;
  }

  if (showWohnenPlusUpsell) {
    return <WohnenPlusUpsellModal onClose={onCloseWohnenPlusUpsell} />;
  }

  if (isFirstTimeWelcome && !initializing) {
    return <SuccessModal onClose={onCloseSuccessModal} variant={isMieterPlusModal ? 'mieten' : 'wohnen'} />;
  }

  return <WohnenPlusLightUpsellModal onClose={onCloseWohnenPlusLight} />;
};

export default memo(WelcomeModal);
